/**
 * O SERVIDOR tem chave VAPID configurada?
 *
 * Existe por causa de um modo de falha específico e caro: sem
 * `VAPID_PUBLIC_KEY`/`VAPID_PRIVATE_KEY` no ambiente, o canal PUSH degrada em
 * **silêncio** (`web_push_service.py:96` devolve string vazia). Se o convite de
 * notificação aparecesse assim mesmo, o usuário concederia permissão e nunca
 * receberia nada.
 *
 * Isso é pior do que não perguntar, por dois motivos:
 *   1. O navegador dá UMA chance. Depois de um "bloquear", não há como
 *      reperguntar por código — a permissão só volta pelas configurações.
 *   2. Ensina que o pedido era inútil, e o próximo pedido legítimo (de qualquer
 *      feature) herda essa desconfiança.
 *
 * `undefined` enquanto a checagem não resolve — quem consome trata como "não
 * convidar ainda", nunca como "pode convidar".
 *
 * Cliente HTTP por `basePath` (Task 6c, achado de 18/09): este hook batia
 * SEMPRE no `api` interno, ignorando `basePath` — no Portal
 * (`basePath = PUSH_BASE_PORTAL`) isso voltava 401 (sem `seweb_auth`) e
 * `temChave` ficava preso em `false` para sempre, escondendo o convite do
 * parceiro mesmo depois do fix em `usePushSubscription.ts` (Task 6b). A
 * escolha do cliente agora vem de `clienteDoPush.ts` — a MESMA função que
 * `usePushSubscription` usa — pra nunca mais divergir. Ver a docstring de
 * lá para o achado completo.
 */
import { useEffect, useState } from "react";

import { clienteDoPush } from "./clienteDoPush";

export function useServerHasVapid(basePath: string): boolean | undefined {
  const [temChave, setTemChave] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        const { data } = await clienteDoPush(basePath).get<{ public_key: string }>(
          `${basePath}/vapid-key`
        );
        if (!cancelado) setTemChave(Boolean(data?.public_key));
      } catch {
        // 403 (parceiro em rota interna), 404, rede fora — em todos os casos a
        // resposta honesta é "não dá para prometer push", não "provavelmente dá".
        if (!cancelado) setTemChave(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [basePath]);

  return temChave;
}
